import React, { useState } from 'react';
import { Alert, Button, Input, Modal } from 'antd';
import { FiCopy } from 'react-icons/fi';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { successAlert } from '@components/toastify/notify-toast';
import styles from '@pages/api-portal/styles.module.scss';
import { ServicePrincipal } from '@pages/api-portal/types';
import { SERVICE_PRINCIPAL_DOMAIN } from '@pages/api-portal/constants';

interface CreatedCredentialsDialogProps {
    principal: ServicePrincipal | null;
    onClose: () => void;
}

/*
 * One-time display of a newly created service principal's auto-generated DataStudio
 * credentials. The password is never retrievable again after this dialog closes.
 */
export function CreatedCredentialsDialog({ principal, onClose }: CreatedCredentialsDialogProps) {
    const [showPassword, setShowPassword] = useState(false);

    const username = principal ? `${principal.name}@${SERVICE_PRINCIPAL_DOMAIN}` : '';

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            successAlert('Copied to clipboard.');
        } catch {
            // Clipboard API unavailable — nothing further to do.
        }
    };

    return (
        <Modal
            title="Service Principal Created"
            visible={!!principal}
            onCancel={onClose}
            wrapClassName="apimModal"
            footer={[
                <Button key="done" type="primary" onClick={onClose}>
                    I have saved the credentials
                </Button>,
            ]}
        >
            <Alert
                style={{ marginBottom: 16 }}
                type="info"
                showIcon
                message="A DataStudio user has been created with ADMIN role. Use these credentials to authenticate."
            />
            <div style={{ marginBottom: 16 }}>
                <label className={styles.fieldLabel}>Service Principal Name</label>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>{principal?.name}</p>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label className={styles.fieldLabel}>Username / Email</label>
                <Input.Group compact style={{ display: 'flex' }}>
                    <Input readOnly value={username} style={{ fontFamily: 'monospace' }} />
                    <Button onClick={() => copyToClipboard(username)}>
                        <FiCopy />
                    </Button>
                </Input.Group>
            </div>
            <div>
                <label className={styles.fieldLabel}>Password</label>
                <Input.Group compact style={{ display: 'flex' }}>
                    <Input
                        readOnly
                        type={showPassword ? 'text' : 'password'}
                        value={principal?.password ?? ''}
                        style={{ fontFamily: 'monospace' }}
                        suffix={
                            showPassword ? (
                                <AiOutlineEyeInvisible onClick={() => setShowPassword((prev) => !prev)} style={{ cursor: 'pointer' }} />
                            ) : (
                                <AiOutlineEye onClick={() => setShowPassword((prev) => !prev)} style={{ cursor: 'pointer' }} />
                            )
                        }
                    />
                    <Button onClick={() => copyToClipboard(principal?.password ?? '')}>
                        <FiCopy />
                    </Button>
                </Input.Group>
            </div>
        </Modal>
    );
}
